import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches rendering errors and displays a retry option.
 *
 * Validates: Requirements 15.6
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            minHeight: '200px',
            textAlign: 'center',
          }}
        >
          <h2 style={{ marginBottom: '1rem', color: 'var(--color-danger)' }}>
            Something went wrong
          </h2>
          <p style={{ marginBottom: '1.5rem', color: 'var(--color-muted)' }}>
            An unexpected error occurred. Please try again.
          </p>
          {this.state.error && (
            <details style={{ marginBottom: '1.5rem', maxWidth: '600px' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--color-muted)' }}>
                Error details
              </summary>
              <pre
                style={{
                  textAlign: 'left',
                  padding: '1rem',
                  backgroundColor: 'var(--color-surface-2)',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '0.75rem',
                }}
              >
                {this.state.error.message}
              </pre>
            </details>
          )}
          <button
            onClick={this.handleRetry}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'var(--color-brand)',
              color: 'var(--color-on-brand)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              minHeight: '44px',
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
